import React, { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, Divider,
  LinearProgress, List, ListItem, ListItemIcon,
  ListItemText, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CelebrationIcon from '@mui/icons-material/Celebration';
import { useHistory } from 'react-router';
import { useAuth } from '../../Contexts/AuthContext';
import { useKnowledgebase } from '../../Contexts/KnowledgebaseContext';
import { getOnboardingStatus } from '../../Helpers/helpApi';
import { ONBOARDING_STEPS } from './onboardingConfig';

export default function OnboardingChecklist() {
  const auth = useAuth();
  const { closePanel } = useKnowledgebase();
  const history = useHistory();
  const [completedSteps, setCompletedSteps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getOnboardingStatus(auth.activeUser?.accessToken)
      .then((res) => {
        if (res?.data?.completed_steps) {
          setCompletedSteps(res.data.completed_steps);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [auth.activeUser?.accessToken]);

  const total = ONBOARDING_STEPS.length;
  const completed = ONBOARDING_STEPS.filter(s => completedSteps.includes(s.key)).length;
  const allDone = completed === total;

  const handleStepClick = (route) => {
    closePanel();
    history.push(route);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  if (allDone) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CelebrationIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
        <Typography variant="h6" gutterBottom>You're all set!</Typography>
        <Typography variant="body2" color="text.secondary">
          You've completed all onboarding steps. Explore the help articles to learn more.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>Getting Started</Typography>
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">{completed} of {total} complete</Typography>
        </Box>
        <LinearProgress variant="determinate" value={(completed / total) * 100} sx={{ borderRadius: 1 }} />
      </Box>
      <List dense disablePadding>
        {ONBOARDING_STEPS.map((step) => {
          const done = completedSteps.includes(step.key);
          return (
            <ListItem
              key={step.key}
              disablePadding
              sx={{ mb: 0.5 }}
              secondaryAction={
                !done && (
                  <Button size="small" onClick={() => handleStepClick(step.route)}>
                    Go
                  </Button>
                )
              }
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {done
                  ? <CheckCircleIcon color="success" fontSize="small" />
                  : <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                }
              </ListItemIcon>
              <ListItemText
                primary={step.label}
                primaryTypographyProps={{
                  variant: 'body2',
                  color: done ? 'text.secondary' : 'text.primary',
                  sx: done ? { textDecoration: 'line-through' } : {},
                }}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}
